<script setup lang="ts">
import type { PlayerMe, PlayerParty, PlayerPartyMember } from '@/api/public'
import FloatingTooltip from '@/components/FloatingTooltip.vue'
import { computed } from 'vue'

type PartySlot = {
  key: string
  member: PlayerPartyMember | null
  name: string
  avatarURL: string
  leader: boolean
  self: boolean
}

const props = defineProps<{
  authenticated: boolean
  player: PlayerMe | null
  party: PlayerParty | null
  loading: boolean
  open: boolean
  friendsOpen: boolean
  selectedMemberSteamId: string
  tooltipsDisabled: boolean
}>()

const emit = defineEmits<{
  openProfile: [steamID: string]
  openGroup: [steamID: string, trigger: HTMLElement | null]
  openFriends: [trigger: HTMLElement | null]
}>()

const slots = computed<PartySlot[]>(() => {
  const members: PartySlot[] = (props.party?.members ?? []).map(member => ({
    key: member.steam_id,
    member,
    name: member.persona_name || member.steam_id,
    avatarURL: member.avatar_url || '',
    leader: member.leader,
    self: member.steam_id === props.player?.steam_id,
  }))
  if (members.length === 0 && props.authenticated && props.player) {
    members.push({
      key: props.player.steam_id,
      member: null,
      name: props.player.persona_name || props.player.steam_id,
      avatarURL: props.player.avatar_url || props.player.avatar_full_url || '',
      leader: true,
      self: true,
    })
  }
  while (members.length < 5) {
    members.push({
      key: `empty-${members.length}`,
      member: null,
      name: 'Свободное место',
      avatarURL: '',
      leader: false,
      self: false,
    })
  }
  return members.slice(0, 5)
})

const occupiedCount = computed(() => props.party?.members.length ?? (props.authenticated ? 1 : 0))
const partyLabel = computed(() => {
  if (!props.authenticated) return 'Группа недоступна без входа'
  return `Группа: ${occupiedCount.value} из 5`
})

function activateSlot(slot: PartySlot, event: MouseEvent): void {
  const trigger = event.currentTarget instanceof HTMLElement
    ? event.currentTarget
    : null
  // A materialized member — including the authenticated player in a durable
  // singleton — opens party management. The synthetic self slot used for a
  // logical solo has no member and keeps its profile shortcut.
  if (slot.member) {
    emit('openGroup', slot.member.steam_id, trigger)
    return
  }
  if (slot.self && props.player?.steam_id) {
    emit('openProfile', props.player.steam_id)
    return
  }
  emit('openFriends', trigger)
}

function slotLabel(slot: PartySlot): string {
  if (slot.self && slot.member) return 'Открыть управление группой'
  if (slot.self) return `Открыть свой профиль: ${slot.name}`
  if (slot.member) return `Открыть действия участника: ${slot.name}`
  return 'Добавить друзей, чтобы собрать группу'
}

function slotTitle(slot: PartySlot): string | undefined {
  if (slot.self && slot.member) return slotLabel(slot)
  return slot.self || slot.member ? slot.name : undefined
}
</script>

<template>
  <div
    class="party-strip"
    :class="{ open, 'friends-open': friendsOpen, loading, solo: occupiedCount === 1 }"
    role="group"
    :aria-label="partyLabel"
    :aria-busy="loading"
  >
    <FloatingTooltip
      v-for="slot in slots"
      :key="slot.key"
      as="span"
      class="party-strip-tooltip-anchor"
      placement="top"
      :max-width="230"
      :offset="10"
      avoid-selector=".player-chat, .player-chat-collapsed-preview"
      dismiss-on-click
      :disabled="
        tooltipsDisabled ||
        loading ||
        Boolean(slot.member) ||
        slot.self ||
        !authenticated
      "
    >
      <button
        class="party-strip-slot"
        type="button"
        :class="{
          occupied: Boolean(slot.member) || slot.self,
          empty: !slot.member && !slot.self,
          leader: slot.leader,
          self: slot.self,
          selected: slot.member?.steam_id === selectedMemberSteamId,
        }"
        :disabled="!authenticated"
        :aria-label="slotLabel(slot)"
        :aria-haspopup="slot.member || !slot.self ? 'dialog' : undefined"
        :aria-controls="
          slot.member
            ? 'player-party-panel'
            : slot.self
              ? undefined
              : 'player-friends-panel'
        "
        :aria-expanded="
          slot.member
            ? slot.self
              ? open
              : open && slot.member.steam_id === selectedMemberSteamId
            : slot.self
              ? undefined
              : friendsOpen
        "
        :data-party-member-steam-id="slot.member?.steam_id"
        :title="slotTitle(slot)"
        @click="activateSlot(slot, $event)"
      >
        <img v-if="slot.avatarURL" class="party-strip-avatar" :src="slot.avatarURL" alt="" />
        <span v-else-if="slot.member || slot.self" class="party-strip-initial" aria-hidden="true">
          {{ slot.name.slice(0, 1).toUpperCase() }}
        </span>
        <img
          v-else
          class="party-strip-empty-icon"
          src="/shared/assets/friend_png.png"
          alt=""
          aria-hidden="true"
        />
        <img
          v-if="slot.leader && occupiedCount >= 2"
          class="party-strip-leader"
          src="/shared/assets/party_leader_overlay_psd.png"
          alt=""
          aria-hidden="true"
        />
      </button>
      <template #content>
        <span class="party-strip-tooltip-copy">
          Добавьте друзей, чтобы собрать группу
        </span>
      </template>
    </FloatingTooltip>
  </div>
</template>
